import React, { memo } from "react";
import AddBoxIcon from "@mui/icons-material/AddBox";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  TextField,
  IconButton,
  Grid,
  Autocomplete,
  Tooltip,
  Typography,
  Box,
} from "@mui/material";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import AddIcon from "@mui/icons-material/Add";

//FUNCTION
function AddCardSales({
  itemList,
  handleAutocompleteChange,
  handleInputChange,
  handleAddItem,
  handleDeleteItem,
  items,
  errors,
}) {
  const highlightMatch = (text, query) => {
    const safeText = text == null ? "" : String(text);
    const safeQuery = query == null ? "" : String(query);

    try {
      const words = safeQuery.toLowerCase().split(" ").filter(Boolean);
      if (!words.length || !safeText) return safeText;

      const escapedWords = words.map((word) =>
        word.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
      );
      const regex = new RegExp(`(${escapedWords.join("|")})`, "gi");

      return safeText.split(regex).map((part, index) => {
        const partText = part ?? "";
        if (!partText) {
          return <React.Fragment key={index} />;
        }
        return words.includes(partText.toLowerCase()) ? (
          <mark key={index} style={{ backgroundColor: "#ffe58f" }}>
            {partText}
          </mark>
        ) : (
          <React.Fragment key={index}>{partText}</React.Fragment>
        );
      });
    } catch (e) {
      console.error("Error in highlightMatch:", e);
      return safeText;
    }
  };

  return (
    <>
      <div>
        <motion.div
          style={{
            width: "100%",
            backgroundColor: "#eeeef0",
            padding: 0,
            borderRadius: "10px",
            border: "none",
            margin: "10px",
            display: "flex",
            justifyContent: "center",
          }}
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.1, delay: 0.1 }}
        >
          <div>
            <Grid container>
              <Link to="/master-entry/item">
                <Tooltip title={"Add item"}>
                  <AddIcon
                    sx={{
                      cursor: "pointer",
                      color: "white",
                      backgroundColor: "grey",
                      margin: "5px",
                    }}
                    justifyContent="flex-end"
                  />
                </Tooltip>
              </Link>
            </Grid>
            {itemList.map((item, index) => (
              <div key={item.id}>
                <Grid
                  container
                  alignItems={"center"}
                  spacing={{ xs: 1, md: 1, lg: 1 }}
                  key={item.id}
                  sx={{ my: 1 }}
                >
                  <Grid item xs={12} sm={6} md={4} columnSpacing={0}>
                    <Autocomplete
                      disablePortal
                      id="combo-box-item"
                      size="small"
                      autoSelect // ✅ Auto-select allowed
                      clearOnBlur={false} // ❌ Prevent clear on blur
                      selectOnFocus
                      handleHomeEndKeys
                      sx={{ backgroundColor: "white" }}
                      value={
                        items.find((it) => it.item_code === item.item_code) ||
                        null
                      }
                      options={items}
                      getOptionLabel={(option) =>
                        option
                          ? `${option.item_name || ""}(${option.item_code || ""})${
                              option.unit ? `-${option.unit}` : ""
                            }`
                          : ""
                      }
                      isOptionEqualToValue={(option, value) =>
                        option.item_code === value.item_code
                      }
                      filterOptions={(options, { inputValue }) => {
                        const words = (inputValue || "")
                          .toLowerCase()
                          .split(" ")
                          .filter(Boolean);
                        return options.filter((option) => {
                          const name = option.item_name?.toLowerCase() || "";
                          const code = option.item_code?.toLowerCase() || "";
                          const unit = option.unit?.toLowerCase() || "";
                          return words.every(
                            (word) =>
                              name.includes(word) ||
                              code.includes(word) ||
                              unit.includes(word)
                          );
                        });
                      }}
                      onInputChange={(event, newInputValue, reason) => {
                        // 🚫 Prevent clearing value on trailing space
                        if (reason === "input") {
                          const trimmed = newInputValue.trimEnd();
                          const matchedItem = items.find(
                            (item) =>
                              item.item_name?.toLowerCase() ===
                              trimmed.toLowerCase()
                          );

                          // If match found, call change handler (optional if needed)
                          if (matchedItem) {
                            handleAutocompleteChange(
                              matchedItem.item_name,
                              item.id
                            );
                          }
                        }
                      }}
                      onChange={(event, newValue) => {
                        handleAutocompleteChange(
                          newValue?.item_name || "",
                          item.id
                        );
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Item Name"
                          name="itemname"
                          value={item.itemname || ""}
                          onChange={(e) => handleInputChange(e, item.id)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              const value = e.target.value.trim();
                              const selectedItem = items.find(
                                (it) =>
                                  it.item_name?.toLowerCase() ===
                                  value.toLowerCase()
                              );
                              if (selectedItem) {
                                handleAutocompleteChange(
                                  selectedItem.item_name,
                                  item.id
                                );
                              }
                            }
                          }}
                          error={errors[`${index}`]?.itemname ? true : false}
                          sx={{ backgroundColor: "white" }}
                        />
                      )}
                      renderOption={(props, option, { inputValue }) => {
                        const label = `${option?.item_name || ""}(${
                          option?.item_code || ""
                        })${option?.unit ? `-${option.unit}` : ""}`;
                        return (
                          <li {...props}>
                            <Box
                              sx={{
                                whiteSpace: "normal",
                                wordWrap: "break-word",
                                maxWidth: "100%",
                              }}
                            >
                              <Typography variant="body2">
                                {highlightMatch(label, inputValue)}
                              </Typography>
                            </Box>
                          </li>
                        );
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      sx={{ backgroundColor: "white" }}
                      size="small"
                      label={item.unit ? item.unit : "Qty"}
                      variant="outlined"
                      type="number"
                      fullWidth
                      name="qty"
                      value={item.qty}
                      InputLabelProps={{
                        shrink: item.qty ? true : false,
                      }}
                      onChange={(e) => handleInputChange(e, item.id)}
                      error={
                        errors[`${index}`] !== undefined
                          ? !!errors[`${index}`].qty
                          : false
                      }
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      sx={{ backgroundColor: "white" }}
                      size="small"
                      label="Rate(₹)"
                      variant="outlined"
                      type="number"
                      fullWidth
                      name="rate"
                      value={item.rate || ""}
                      onChange={(e) => handleInputChange(e, item.id)}
                    />
                  </Grid>
                
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      sx={{ backgroundColor: "white" }}
                      size="small"
                      label="Amount(₹)"
                      variant="outlined"
                      type="number"
                      fullWidth
                      name="amount"
                      value={item.amount}
                      InputLabelProps={{ shrink: item.amount ? true : false }}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={2}>
                    <Tooltip title={"Add item"}>
                      <IconButton onClick={() => handleAddItem(item.id)}>
                        <AddBoxIcon fontSize="medium" sx={{ color: "green" }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={"Delete item"}>
                      <IconButton
                        hidden={itemList.length > 1 ? false : true}
                        onClick={() => handleDeleteItem(item)}
                      >
                        <DeleteIcon
                          fontSize="medium"
                          sx={{ color: "#d43529" }}
                        />
                      </IconButton>
                    </Tooltip>
                  </Grid>

                </Grid>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </>
  );
}

export default memo(AddCardSales);
